import React, { useEffect, useState } from 'react';
import {
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CodeBracketIcon,
  DocumentTextIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import CitedMarkdown from '../../../components/markdown/CitedMarkdown';

// CitedMarkdown renders up to three heading levels, so nesting stops there.
const MAX_HEADING_DEPTH = 3;
// Reports are read in full; the "read more" fold would only get in the way.
const NO_FOLD = Number.MAX_SAFE_INTEGER;

const isScalar = (value) => (
  typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
);

const humanize = (key) => String(key)
  .replace(/[_-]+/g, ' ')
  .trim()
  .replace(/^./, (char) => char.toUpperCase());

/**
 * Render a custom report's content as markdown.
 *
 * The content is whatever the prompt returned: markdown text, or JSON whose keys
 * carry the structure. JSON keys become headings (or bold labels for one-liners)
 * so a structured answer reads like a report instead of a data dump.
 */
const contentToMarkdown = (value, depth = 2) => {
  if (value == null || value === '') return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);

  if (Array.isArray(value)) {
    if (value.every(isScalar)) {
      return value.filter((item) => item !== '').map((item) => `- ${item}`).join('\n');
    }
    return value.map((item) => contentToMarkdown(item, depth)).filter(Boolean).join('\n\n');
  }

  if (typeof value === 'object') {
    return Object.entries(value)
      .map(([key, item]) => {
        const body = contentToMarkdown(item, Math.min(depth + 1, MAX_HEADING_DEPTH));
        if (!body) return '';
        if (isScalar(item)) return `**${humanize(key)}:** ${body}`;
        return `${'#'.repeat(depth)} ${humanize(key)}\n\n${body}`;
      })
      .filter(Boolean)
      .join('\n\n');
  }

  return String(value);
};

// A verification appendix is the analyst's working, not part of the report: it
// is where a prompt records which extract each key point rests on. It stays out
// of the reading view, and one click away for whoever is checking a citation.
const APPENDIX_HEADING = /^#{1,3}\s*Verification appendix\b/i;

const splitAppendix = (markdown) => {
  const lines = String(markdown || '').split('\n');
  const at = lines.findIndex((line) => APPENDIX_HEADING.test(line.trim()));
  if (at === -1) return { body: markdown, appendix: '' };
  // The heading itself stays out: the toggle that opens the appendix is already
  // its label, and rendering both reads as the title written twice.
  const after = lines.slice(at + 1);
  const nextReportHeading = after.findIndex((line) => (
    /^##\s+/.test(line.trim()) && !APPENDIX_HEADING.test(line.trim())
  ));
  if (at === 0 && nextReportHeading !== -1) {
    return {
      appendix: after.slice(0, nextReportHeading).join('\n').trim(),
      body: after.slice(nextReportHeading).join('\n').trim(),
    };
  }
  return {
    body: lines.slice(0, at).join('\n').trimEnd(),
    appendix: after.join('\n').trim(),
  };
};

const rawText = (content) => {
  if (typeof content === 'string') return content;
  try {
    return JSON.stringify(content, null, 2);
  } catch {
    return String(content);
  }
};

/**
 * Text of a report as it is edited by hand.
 *
 * A text report is edited as the markdown it already is. A structured report is
 * edited as its JSON, because the keys are what gives it its shape: rewriting
 * the rendered text would throw that away.
 */
const isStructured = (content) => content != null && typeof content === 'object';

/** Turn edited text back into content of the shape the report was generated in. */
const parseEdited = (text, original) => {
  if (!isStructured(original)) return text;
  return JSON.parse(text);
};

/**
 * Reading view for a generated custom report: rendered text by default, with the
 * raw prompt output one click away for anyone checking the JSON shape.
 *
 * A report is one analysis of every selected transcript, held in `content`.
 * Reports generated before that change hold one block per session instead, and
 * are still rendered from `sessions`.
 *
 * Wording is edited here, per language: `onSave` stores the text shown, so the
 * English and the French version each keep their own corrections.
 */
const CustomReportOutput = ({
  output,
  title = '',
  languageLabel = '',
  editable = false,
  onSave,
  transcriptReferences,
  onCitationClick,
}) => {
  const [showRaw, setShowRaw] = useState(false);
  const [showAppendix, setShowAppendix] = useState(false);
  // Which section is open in the editor: null for none, otherwise the session
  // index, or 'combined' for a report held in a single content block.
  const [editingKey, setEditingKey] = useState(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState('');

  const combined = output && 'content' in output ? output.content : null;
  const sources = output?.sources || [];
  const sessions = output?.sessions || [];

  // Switching language (or regenerating) replaces the text under the editor.
  useEffect(() => {
    setEditingKey(null);
    setDraft('');
    setEditError('');
    setShowAppendix(false);
  }, [output]);

  if (combined == null && sessions.length === 0) return null;

  const startEdit = (key, content) => {
    setEditingKey(key);
    setDraft(rawText(content ?? ''));
    setEditError('');
    setShowRaw(false);
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setDraft('');
    setEditError('');
  };

  const save = async (original, sessionIndex) => {
    let content;
    try {
      content = parseEdited(draft, original);
    } catch {
      setEditError('This report is stored as JSON, so the text has to stay valid JSON.');
      return;
    }
    setSaving(true);
    setEditError('');
    try {
      await onSave?.({ content, sessionIndex });
      setEditingKey(null);
      setDraft('');
    } catch (err) {
      setEditError(err?.message || 'Could not save your changes');
    } finally {
      setSaving(false);
    }
  };

  const renderMarkdown = (text, key) => (
    <CitedMarkdown
      key={key}
      text={text}
      maxLength={NO_FOLD}
      className="custom-report-body"
      transcriptReferences={transcriptReferences}
      onCitationClick={onCitationClick}
    />
  );

  const renderReport = (content, key) => {
    const { body, appendix } = splitAppendix(contentToMarkdown(content));
    return (
      <>
        {renderMarkdown(body, `${key}-body`)}
        {appendix && (
          <div className="custom-report-appendix">
            <button
              type="button"
              className="custom-reports-link"
              onClick={() => setShowAppendix((prev) => !prev)}
            >
              {showAppendix ? <ChevronUpIcon /> : <ChevronDownIcon />}
              {showAppendix ? 'Hide verification appendix' : 'Show verification appendix'}
            </button>
            {showAppendix && renderMarkdown(appendix, `${key}-appendix`)}
          </div>
        )}
      </>
    );
  };

  const editButton = (key, content) => (
    <button
      type="button"
      className="custom-reports-link"
      onClick={() => startEdit(key, content)}
    >
      <PencilSquareIcon />
      Edit text
    </button>
  );

  const editor = (original, sessionIndex) => (
    <div className="custom-report-edit">
      <textarea
        className="custom-reports-control custom-report-edit-area"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        rows={20}
        spellCheck
        disabled={saving}
      />
      <p className="custom-reports-note">
        {isStructured(original)
          ? 'Edit the JSON: change the values, keep the keys and the brackets.'
          : 'Markdown is kept as it is written here.'}
        {languageLabel
          ? ` Saved for ${languageLabel} only — the other languages keep their own text.`
          : ' Saved as the original text; existing translations are marked out of date.'}
      </p>
      {editError && <div className="custom-reports-error">{editError}</div>}
      <div className="custom-report-edit-actions">
        <button
          type="button"
          className="custom-reports-btn custom-reports-btn-primary"
          onClick={() => save(original, sessionIndex)}
          disabled={saving}
        >
          {saving ? <ArrowPathIcon className="custom-reports-spin" /> : null}
          {saving ? 'Saving...' : 'Save text'}
        </button>
        <button
          type="button"
          className="custom-reports-btn"
          onClick={cancelEdit}
          disabled={saving}
        >
          Cancel
        </button>
      </div>
    </div>
  );

  return (
    <div className="custom-report-output">
      <div className="custom-report-output-head">
        <h3>{output.report_title || title}</h3>
        {languageLabel && <span className="custom-report-output-lang">{languageLabel}</span>}
        {output.edited_at && (
          <span className="custom-report-output-lang" title={output.edited_at}>Edited</span>
        )}
        <div className="custom-report-output-tools">
          {editable && combined != null && editingKey === null && editButton('combined', combined)}
          <button
            type="button"
            className="custom-reports-link"
            onClick={() => setShowRaw((prev) => !prev)}
          >
            {showRaw ? <DocumentTextIcon /> : <CodeBracketIcon />}
            {showRaw ? 'Show text' : 'Show raw output'}
          </button>
        </div>
      </div>

      {combined != null ? (
        <section>
          {sources.length > 1 && (
            <p className="custom-report-output-sources">
              One analysis across {sources.length} sessions:{' '}
              {sources.map((source) => source.session_title).join(' · ')}
            </p>
          )}
          {editingKey === 'combined' ? (
            editor(combined, null)
          ) : showRaw ? (
            <pre>{rawText(combined)}</pre>
          ) : (
            renderReport(combined, 'combined')
          )}
        </section>
      ) : (
        sessions.map((session, index) => (
          <section key={session.session_id || session.session_title}>
            <div className="custom-report-output-section-head">
              <h4>{session.session_title}</h4>
              {editable && editingKey === null && editButton(index, session.content)}
            </div>
            {editingKey === index ? (
              editor(session.content, index)
            ) : showRaw ? (
              <pre>{rawText(session.content)}</pre>
            ) : (
              renderReport(session.content, `session-${index}`)
            )}
          </section>
        ))
      )}
    </div>
  );
};

export default CustomReportOutput;
